import { createContext, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import emptyFunction from 'react-utils/emptyFunction';

/**
 * Context for determining whether timeline events are collapsed or expanded by default (i.e. according to UserSettings)
 * @param {Boolean}    value.isCollapsedByDefault
 */

/**
 * to create the context:
 * <TimelineCollapsedContext.Provider value={...}>...</TimelineCollapsedContext.Provider>
 */
export const TimelineCollapsedContext = /*#__PURE__*/createContext(undefined);
TimelineCollapsedContext.Provider.propTypes = {
  value: PropTypes.bool
};

/**
 * @argument onUpdateCollapsedContext a function that is called when the context is initialized and when it updates (e.g. a state setter function)
 * @argument shouldExpandPinnedEngagement a boolean that determines whether or not the call to the onUpdateCollapsedContext is skipped on initialization
 *
 * @example
 * useTimelineCollapsedContext(setIsEventCollapsed);
 *
 * @example if reference to isCollapsedByDefault is needed (e.g. WithTimelineCollapsedContext)
 * const { isCollapsedByDefault } = useTimelineCollapsedContext();
 *
 */
export const useTimelineCollapsedContext = (onUpdateCollapsedContext = emptyFunction, shouldExpandPinnedEngagement = false) => {
  const isCollapsedByDefault = useContext(TimelineCollapsedContext);
  const hasCompletedInitialization = useRef(false);

  // invoke callback when the collapsed context changes (i.e. when the UserSetting changes)
  useEffect(() => {
    if (shouldExpandPinnedEngagement && !hasCompletedInitialization.current) {
      hasCompletedInitialization.current = true;
      return;
    }
    onUpdateCollapsedContext(isCollapsedByDefault);
  }, [isCollapsedByDefault, onUpdateCollapsedContext, shouldExpandPinnedEngagement]);
  return isCollapsedByDefault;
};